// src/components/integrations/Authorize.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import api from "../services/api";
import { Mail, Shield, CheckCircle, AlertCircle } from "lucide-react";

const Authorize = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser, refreshUser } = useUser(); // Changed from 'user' to 'currentUser'
  const [authorizing, setAuthorizing] = useState(false);
  const [error, setError] = useState("");
  const [authLoading, setAuthLoading] = useState(true);

  const client_id = searchParams.get("client_id");
  const redirect_uri = searchParams.get("redirect_uri");
  const state = searchParams.get("state");

  useEffect(() => {
    // Check if user is loaded
    setAuthLoading(false);

    // If user is not logged in, redirect to login with return URL
    if (!currentUser) {
      const returnUrl = `/integrations/authorize?${searchParams.toString()}`;
      console.log("No user found, redirecting to login");
      navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
    }
  }, [currentUser, navigate, searchParams]);

  const handleAuthorize = async () => {
    try {
      setAuthorizing(true);
      setError("");

      const response = await api.completeAuthorization({
        client_id,
      });

      if (response.success && response.redirectUrl) {
        // Redirect back to Lead Management System
        window.location.href = response.redirectUrl;
      } else {
        setError("Authorization failed");
        setAuthorizing(false);
      }
    } catch (error) {
      console.error("Authorization error:", error);
      setError(
        error.response?.data?.message || "Failed to authorize integration"
      );
      setAuthorizing(false);
    }
  };

  const handleCancel = () => {
    try {
      const redirectUrl = new URL(redirect_uri);
      redirectUrl.searchParams.set("error", "access_denied");
      redirectUrl.searchParams.set("state", state);
      window.location.href = redirectUrl.toString();
    } catch (error) {
      console.error("Invalid redirect URI:", error);
      navigate("/");
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0B0F]">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return null; // Will redirect to login
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0B0F] p-4">
      <div className="max-w-md w-full bg-[#1A1B1F] rounded-xl p-8 border border-gray-700 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-center mb-6">
          <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center">
            <Shield className="w-8 h-8 text-white" />
          </div>
        </div>

        <h1 className="text-2xl font-bold text-white mb-2 text-center">
          Authorize Integration
        </h1>
        <p className="text-gray-400 text-center mb-6">
          Connect your Call Recording System to Lead Management
        </p>

        {/* User Info */}
        <div className="bg-[#0C0E12] rounded-lg p-4 mb-6 border border-gray-700">
          <p className="text-sm text-gray-400 mb-2">Your Account:</p>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
              <span className="text-white font-semibold text-sm">
                {currentUser.email?.[0]?.toUpperCase() || "U"}
              </span>
            </div>
            <div>
              <p className="text-white font-medium">{currentUser.email}</p>
              <p className="text-gray-400 text-sm">
                {currentUser.companyName ||
                  currentUser.company?.name ||
                  "Your Company"}
              </p>
            </div>
          </div>
        </div>

        {/* Permissions */}
        <div className="bg-blue-900/10 border border-blue-700/50 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-2 mb-3">
            <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-300 font-medium">
              This will allow the Lead Management System to:
            </p>
          </div>
          <ul className="space-y-2 ml-7">
            <li className="flex items-start gap-2 text-sm text-blue-200">
              <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Access call recordings and transcripts</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-blue-200">
              <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Receive call insights and AI qualifications</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-blue-200">
              <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Match calls to leads by phone number</span>
            </li>
          </ul>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleCancel}
            disabled={authorizing}
            className="flex-1 px-4 py-3 bg-[#0C0E12] border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-800 hover:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleAuthorize}
            disabled={authorizing}
            className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium shadow-lg shadow-purple-500/20"
          >
            {authorizing ? (
              <span className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                Authorizing...
              </span>
            ) : (
              "Authorize"
            )}
          </button>
        </div>

        {/* Security Notice */}
        <p className="text-xs text-gray-500 text-center mt-6">
          By authorizing, you agree to share call data with the Lead Management
          System. You can revoke access at any time from your settings.
        </p>
      </div>
    </div>
  );
};

export default Authorize;
